import { createFileRoute, redirect } from "@tanstack/react-router";
import { getMyRole } from "@/lib/dr-sara.functions";
import { queryOptions, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X } from "lucide-react";
import { listAdminServices, upsertService, deleteService, toggleServiceActive } from "@/lib/services-admin.functions";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";

const listQ = () => queryOptions({ queryKey: ["admin", "services"], queryFn: () => listAdminServices() });

export const Route = createFileRoute("/_authenticated/admin/services")({
  beforeLoad: async ({ context }) => {
    const role = await context.queryClient.ensureQueryData(
      queryOptions({ queryKey: ["me", "role"], queryFn: () => getMyRole() }),
    );
    if (!role.isAdmin) throw redirect({ to: "/admin" });
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(listQ()),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: ServicesAdmin,
});

type Row = {
  id?: string;
  slug: string;
  title_ar: string;
  description_ar: string;
  duration_min: number;
  price_usd: number;
  is_active: boolean;
  sort_order: number;
};

const blank = (): Row => ({
  slug: "",
  title_ar: "",
  description_ar: "",
  duration_min: 45,
  price_usd: 70,
  is_active: true,
  sort_order: 0,
});

function ServicesAdmin() {
  const { data } = useSuspenseQuery(listQ());
  const [editing, setEditing] = useState<Row | null>(null);
  const qc = useQueryClient();
  const del = useServerFn(deleteService);
  const toggle = useServerFn(toggleServiceActive);

  async function remove(id: string) {
    if (!window.confirm("حذف هذه الخدمة نهائياً؟")) return;
    try {
      await del({ data: { id } });
      toast.success("تم الحذف");
      qc.invalidateQueries({ queryKey: ["admin", "services"] });
      qc.invalidateQueries({ queryKey: ["public", "services"] });
    } catch (e: any) {
      toast.error(e?.message ?? "فشل الحذف — قد يكون هناك حجوزات مربوطة بها.");
    }
  }

  async function flip(row: any) {
    try {
      await toggle({ data: { id: row.id, is_active: !row.is_active } });
      qc.invalidateQueries({ queryKey: ["admin", "services"] });
      qc.invalidateQueries({ queryKey: ["public", "services"] });
    } catch (e: any) {
      toast.error(e?.message ?? "فشل");
    }
  }

  return (
    <div>
      <header className="mb-6 flex items-center justify-between gap-4">
        <div>
          <p className="kicker text-modern-orange">إدارة المحتوى</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-foreground">إعدادات الاستشارة</h1>
          <p className="mt-1 text-sm text-muted-foreground">الأسعار بالدولار الأمريكي.</p>
        </div>
        <button
          onClick={() => setEditing(blank())}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 font-display text-sm font-bold text-primary-foreground shadow-soft hover:bg-primary-hover"
        >
          <Plus className="h-4 w-4" /> أضف خدمة
        </button>
      </header>

      {data.length === 0 ? (
        <div className="rounded-ds-lg border border-dashed border-border bg-card/50 p-12 text-center">
          <p className="text-muted-foreground">لا خدمات مضافة بعد.</p>
          <button
            onClick={() => setEditing(blank())}
            className="mt-4 inline-flex rounded-full bg-primary px-5 py-2 font-display text-sm font-bold text-primary-foreground shadow-soft hover:bg-primary-hover"
          >
            أضف أول خدمة
          </button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl bg-card shadow-soft">
          <table className="w-full text-start text-sm">
            <thead className="bg-background/70 text-xs text-muted-foreground">
              <tr>
                <th className="p-4 text-start font-semibold">الاسم</th>
                <th className="p-4 text-start font-semibold">المدة</th>
                <th className="p-4 text-start font-semibold">السعر</th>
                <th className="p-4 text-start font-semibold">الحالة</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((s: any) => (
                <tr key={s.id} className="border-t border-border">
                  <td className="p-4">
                    <p className="font-display font-bold text-foreground">{s.title_ar}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{s.slug}</p>
                  </td>
                  <td className="p-4">{s.duration_min} دقيقة</td>
                  <td className="p-4">${Number(s.price_usd).toFixed(2)}</td>
                  <td className="p-4">
                    <button
                      onClick={() => flip(s)}
                      className={`rounded-full px-3 py-1 text-xs font-bold ${s.is_active ? "bg-soft-green/40 text-foreground" : "bg-muted text-muted-foreground"}`}
                    >
                      {s.is_active ? "نشطة" : "متوقفة"}
                    </button>
                  </td>
                  <td className="p-4 text-end">
                    <button
                      onClick={() => setEditing(s)}
                      className="me-2 inline-flex items-center gap-1 rounded-full border border-input bg-background px-3 py-1.5 text-xs font-bold hover:bg-accent/15"
                    >
                      <Pencil className="h-3 w-3" /> عدّل
                    </button>
                    <button
                      onClick={() => remove(s.id)}
                      className="inline-flex items-center gap-1 rounded-full border border-destructive/40 bg-background px-3 py-1.5 text-xs font-bold text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" /> احذف
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <Editor row={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function Editor({ row, onClose }: { row: Row; onClose: () => void }) {
  const [form, setForm] = useState<Row>(row);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const save = useServerFn(upsertService);

  async function submit() {
    if (!form.slug || !form.title_ar) {
      toast.error("الرجاء تعبئة الرمز والاسم.");
      return;
    }
    setBusy(true);
    try {
      await save({
        data: {
          id: form.id,
          slug: form.slug.trim(),
          title_ar: form.title_ar.trim(),
          description_ar: form.description_ar ?? "",
          duration_min: Number(form.duration_min),
          price_usd: Number(form.price_usd),
          is_active: form.is_active,
          sort_order: form.sort_order,
        },
      });
      toast.success("تم الحفظ");
      qc.invalidateQueries({ queryKey: ["admin", "services"] });
      qc.invalidateQueries({ queryKey: ["public", "services"] });
      onClose();
    } catch (e: any) {
      toast.error(e?.message ?? "فشل الحفظ");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-foreground/30 backdrop-blur-sm" />
      <aside onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-xl overflow-y-auto bg-card p-6 shadow-lift">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold text-foreground">
            {form.id ? "تعديل الخدمة" : "خدمة جديدة"}
          </h2>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-accent/20" aria-label="إغلاق">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="الاسم بالعربية" required>
              <input value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })} className={inputCls} />
            </Field>
            <Field label="الرمز (slug) — بالإنجليزية" required>
              <input dir="ltr" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} className={inputCls} placeholder="initial-consultation" />
            </Field>
          </div>
          <Field label="الوصف">
            <textarea value={form.description_ar} onChange={(e) => setForm({ ...form, description_ar: e.target.value })} rows={4} className={inputCls} />
          </Field>
          <div className="grid gap-4 md:grid-cols-3">
            <Field label="المدة (دقيقة)" required>
              <input type="number" min={5} value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: Number(e.target.value) })} className={inputCls} />
            </Field>
            <Field label="السعر (USD)" required>
              <input type="number" min={0} step="0.01" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: Number(e.target.value) })} className={inputCls} />
            </Field>
            <Field label="الترتيب">
              <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })} className={inputCls} />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            <span>نشطة (تظهر للعملاء)</span>
          </label>
          <div className="pt-4">
            <button
              onClick={submit}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 font-display text-sm font-bold text-primary-foreground shadow-soft hover:bg-primary-hover disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ الخدمة
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

const inputCls =
  "w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30";

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-semibold text-foreground">
        {label}
        {required && <span className="text-modern-orange"> *</span>}
      </span>
      {children}
    </label>
  );
}